// Static seed data — stands in for the achievement + earners endpoints until the
// backend lands. Shapes mirror the eventual DTOs so the service swap is trivial.

import {
  ACHIEVEMENT_TIERS,
  achievementMedalDataUri,
  type TAchievementTier,
} from '@/core/utils/achievement-medal'
import type { TAchievementUnlockRule } from '@/core/utils/achievement-unlock'
import type {
  TAchievementEarner,
  TIndividualAchievement,
} from '@/modules/individual-achievement/individual-achievement.types'

const TIER_ORDER: TAchievementTier[] = ['silver', 'gold', 'diamond']
const LEVELS = [1, 2, 3, 4]

// Correct-answer thresholds per (tier, level) — the base kazanım koşulu each
// seeded badge ships with.
const CORRECT_ANSWER_THRESHOLDS: Record<TAchievementTier, number[]> = {
  silver: [50, 150, 300, 500],
  gold: [750, 1200, 2000, 3000],
  diamond: [5000, 7500, 12000, 20000],
}

// Win-streak requirement stacked on the level-4 badge of each tier, so the
// multi-rule (AND) rendering has a fixture to exercise.
const WIN_STREAK_THRESHOLDS: Record<TAchievementTier, number> = {
  silver: 3,
  gold: 5,
  diamond: 10,
}

// Start existing badges with the category-scoped correct-answer metric. The
// category intentionally stays empty so the admin selects a live backend
// category while editing instead of persisting a hardcoded category id.
function seedUnlockRules(
  tier: TAchievementTier,
  level: number
): TAchievementUnlockRule[] {
  const rules: TAchievementUnlockRule[] = [
    {
      id: `${tier}-${level}-correct-answers`,
      metric: 'category_correct_answers',
      category_id: null,
      operator: 'gte',
      value: CORRECT_ANSWER_THRESHOLDS[tier][level - 1] ?? 50,
    },
  ]
  if (level === 4) {
    rules.push({
      id: `${tier}-${level}-win-streak`,
      metric: 'match_win_streak',
      category_id: null,
      operator: 'gte',
      value: WIN_STREAK_THRESHOLDS[tier],
    })
  }
  return rules
}

// One ISO acquire date per (tier, level) so the fixtures are deterministic
// (no Date.now — keeps the seed reproducible).
const DAYS = ['05', '12', '19', '26']

export const INDIVIDUAL_ACHIEVEMENTS_SEED: TIndividualAchievement[] =
  TIER_ORDER.flatMap((tier, tierIdx) =>
    LEVELS.map((level) => {
      const label = ACHIEVEMENT_TIERS[tier].label
      const month = String(3 + tierIdx).padStart(2, '0') // 03..05
      return {
        id: `${tier}-${level}`,
        name: `${label} ${level}`,
        description: `${label} seviye ${level} başarımı. Bu rozeti kazanmak için ilgili koşulları tamamla.`,
        image: achievementMedalDataUri(tier),
        tier,
        unlock_rules: seedUnlockRules(tier, level),
        acquired_at: `2026-${month}-${DAYS[level - 1]}T09:30:00.000Z`,
        // Keep a couple passive so the aktif/pasif filter has something to show.
        is_active: !(tier === 'diamond' && level >= 3),
      }
    })
  )

// Demo player pool for the detail screen (earned = did this player earn it).
const PLAYER_POOL = [
  {
    id: 'p1',
    name: 'Ahmet Yılmaz',
    email: 'ahmet.yilmaz@bilgilig.com',
    avatar: 'system_avatar_1',
  },
  {
    id: 'p2',
    name: 'Zeynep Kaya',
    email: 'zeynep.kaya@bilgilig.com',
    avatar: 'system_avatar_2',
  },
  {
    id: 'p3',
    name: 'Mehmet Demir',
    email: 'mehmet.demir@bilgilig.com',
    avatar: 'system_avatar_3',
  },
  {
    id: 'p4',
    name: 'Elif Şahin',
    email: 'elif.sahin@bilgilig.com',
    avatar: 'system_avatar_4',
  },
  {
    id: 'p5',
    name: 'Can Öztürk',
    email: 'can.ozturk@bilgilig.com',
    avatar: 'system_avatar_5',
  },
  {
    id: 'p6',
    name: 'Derya Aydın',
    email: 'derya.aydin@bilgilig.com',
    avatar: 'system_avatar_6',
  },
]

// Deterministic per-achievement earned flags so the detail feels populated.
export function earnersForAchievement(id: string): TAchievementEarner[] {
  const seed = [...id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
  return PLAYER_POOL.map((player, index) => ({
    ...player,
    earned: (seed + index) % 2 === 0,
  }))
}
